import React, { useEffect, useState } from "react";
import type { User } from "firebase/auth";
import { sendEmailVerification } from "firebase/auth";
import { DEFAULT_PROFESSIONAL_VERIFICATION } from "./types/professionalVerification";
import Layout from "./components/Layout";
import LoginScreen from "./components/LoginScreen";
import SubscribeModal, { type SubscribeBilling } from "./components/SubscribeModal";
import LandingPage from "./components/LandingPage";
import AdminPanel from "./components/AdminPanel";

import PatientModule from "./components/PatientModule";
import AgendaModule from "./components/AgendaModule";
import ConsultModule from "./components/ConsultModule";
import AtlasModule from "./components/AtlasModule";
import DecisionModule from "./components/DecisionModule";
import BureaucracyModule from "./components/BureaucracyModule";
import ClinicalRegistryModule from "./components/ClinicalRegistryModule";
import SupportCenterModule from "./components/SupportCenterModule";
import VerifyReportPage from "./components/VerifyReportPage";
import PrivacyPolicyPage from "./components/PrivacyPolicyPage";
import SupportPage from "./components/SupportPage";
import TermsOfUsePage from "./components/TermsOfUsePage";
import DiagnosticErrorBoundary from "./components/DiagnosticErrorBoundary";

import { AppTab } from "./contexts/types";
import { LanguageProvider, useLanguage } from "./contexts/LanguageContext";
import { PatientProvider } from "./contexts/PatientContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { useUserPlan } from "./contexts/useUserPlan";

import { getAdminAccessClaim, isAdminAccessAllowed } from "./utils/adminAccess";

type PlanInfo = {
  tier: string;
  status: string;
  role?: string;
};

type SubscribeTier = "pro" | "premium";

// Gate baseado exclusivamente em auth.currentUser.emailVerified (após reload()),
// independente do provedor de login (senha, Google ou Apple) — ver item 3 da auditoria de verificação de e-mail.
function requiresEmailVerificationAccess(user: User | null) {
  return Boolean(user && !user.emailVerified);
}

const RESEND_COOLDOWN_SECONDS = 60;

const EmailVerificationGate: React.FC<{
  currentUser: User;
  onRefreshUser: () => Promise<User | null>;
  onUseAnotherAccount: () => Promise<void>;
}> = ({ currentUser, onRefreshUser, onUseAnotherAccount }) => {
  const { t } = useLanguage();
  const [isSending, setIsSending] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSwitchingAccount, setIsSwitchingAccount] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(0);

  const email = currentUser.email?.trim() || "";
  const isPasswordAccount = currentUser.providerData?.some((provider) => provider.providerId === "password");

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setTimeout(() => setCooldown((seconds) => Math.max(0, seconds - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  const handleResend = async () => {
    if (cooldown > 0) return;

    setIsSending(true);
    setError("");
    setNotice("");

    try {
      await sendEmailVerification(currentUser);
      setNotice(
        email
          ? t("verificationEmailResentTo", { email })
          : t("verifyEmail")
      );
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      const code = typeof err === "object" && err && "code" in err ? String((err as { code?: string }).code || "") : "";
      console.error("[auth] resend verification email failed:", code || err);

      if (code === "auth/too-many-requests") {
        setError(t("verificationTooManyRequests"));
        setCooldown(RESEND_COOLDOWN_SECONDS);
      } else if (code === "auth/user-token-expired" || code === "auth/requires-recent-login") {
        setError(t("verificationRequiresRecentLogin"));
      } else if (code === "auth/network-request-failed") {
        setError(t("verificationNetworkFailed"));
      } else if (!isPasswordAccount) {
        setError(t("verificationFederatedSendFailed"));
      } else {
        setError(t("verificationEmailResendFailed"));
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    setError("");
    setNotice("");

    try {
      const refreshedUser = await onRefreshUser();
      if (refreshedUser?.emailVerified) {
        setNotice(t("emailAlreadyVerified"));
      } else {
        setError(t("emailVerificationRequired"));
      }
    } catch (err) {
      console.error("[auth] refresh verification status failed:", err);
      setError(t("loginAccessDenied"));
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleUseAnotherAccount = async () => {
    setIsSwitchingAccount(true);
    try {
      await onUseAnotherAccount();
    } finally {
      setIsSwitchingAccount(false);
    }
  };

  const isBusy = isSending || isRefreshing || isSwitchingAccount;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[radial-gradient(circle_at_top,_rgba(56,189,248,0.18),_transparent_34%),linear-gradient(180deg,_#07111f_0%,_#030712_100%)] px-4 py-10">
      <div className="w-full max-w-xl rounded-3xl border border-cyan-500/15 bg-slate-950/80 px-6 py-8 shadow-2xl shadow-cyan-950/20 backdrop-blur">
        <div className="mb-6">
          <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-cyan-300/80">
            OrthoPrecision
          </p>
          <h1 className="mt-3 text-3xl font-black tracking-tight text-white">
            {t("verifyEmail")}
          </h1>
          <p className="mt-3 text-sm leading-6 text-slate-300">
            {t("emailVerificationRequired")}
          </p>
          <p className="mt-2 text-xs leading-5 text-slate-400">
            {t("verificationSpamHint")}
          </p>
        </div>

        <div className="rounded-2xl border border-white/8 bg-white/5 px-4 py-4 text-sm text-slate-200">
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400">
            E-mail da conta
          </div>
          <div className="mt-1 break-all font-medium text-white">
            {email || currentUser.uid}
          </div>
        </div>

        {notice && (
          <div className="mt-4 rounded-2xl border border-emerald-500/20 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">
            {notice}
          </div>
        )}

        {error && (
          <div className="mt-4 rounded-2xl border border-amber-500/20 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
            {error}
          </div>
        )}

        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <button
            type="button"
            onClick={handleResend}
            disabled={isBusy || cooldown > 0}
            className="inline-flex items-center justify-center rounded-2xl border border-cyan-400/20 bg-cyan-500/10 px-4 py-3 text-sm font-bold text-cyan-50 transition-colors hover:bg-cyan-500/15 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSending
              ? "..."
              : cooldown > 0
                ? t("resendVerificationEmailCountdown", { seconds: cooldown })
                : t("resendVerificationEmail")}
          </button>

          <button
            type="button"
            onClick={handleRefresh}
            disabled={isBusy}
            className="inline-flex items-center justify-center rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-bold text-slate-100 transition-colors hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isRefreshing ? "..." : t("verificationAlreadyConfirmed")}
          </button>

          <button
            type="button"
            onClick={handleUseAnotherAccount}
            disabled={isBusy}
            className="inline-flex items-center justify-center rounded-2xl border border-white/10 bg-transparent px-4 py-3 text-sm font-bold text-slate-400 transition-colors hover:bg-white/5 hover:text-slate-200 disabled:cursor-not-allowed disabled:opacity-60 sm:col-span-2"
          >
            {isSwitchingAccount ? "..." : t("useAnotherAccount")}
          </button>
        </div>
      </div>
    </div>
  );
};

const AppWorkspace: React.FC<{
  currentUser: User;
  onLogout: () => Promise<void>;
  plan: PlanInfo;
  isProfessionalAccessBypassed: boolean;
  onOpenSubscribeClick?: (tier?: SubscribeTier) => void;
  activeTab: AppTab;
  onNavigateToTab: (tab: AppTab) => void;
}> = ({
  currentUser,
  onLogout,
  plan,
  isProfessionalAccessBypassed,
  onOpenSubscribeClick,
  activeTab,
  onNavigateToTab,
}) => {
  const renderContent = () => {
    switch (activeTab) {
      case AppTab.PATIENTS:
        return (
          <PatientModule
            onNavigateToTab={onNavigateToTab}
            currentUser={currentUser.email || currentUser.uid}
          />
        );
      case AppTab.AGENDA:
        return <AgendaModule onNavigateToTab={onNavigateToTab} />;
      case AppTab.REPORT_ANALYSIS:
        return <ConsultModule moduleView="report_analysis" />;
      case AppTab.DECISION:
        return <DecisionModule />;
      case AppTab.CONSULT:
        return <ConsultModule moduleView="pathologies" />;
      case AppTab.ATLAS:
        return <AtlasModule />;
      case AppTab.BUREAUCRACY:
        return <BureaucracyModule />;
      case AppTab.CLINICAL_REGISTRY:
        return <ClinicalRegistryModule />;
      case AppTab.SUPPORT_CENTER:
        return <SupportCenterModule currentModule={activeTab} />;
      default:
        return (
          <PatientModule
            onNavigateToTab={onNavigateToTab}
            currentUser={currentUser.email || currentUser.uid}
          />
        );
    }
  };

  if (typeof window !== "undefined") {
    console.log("[AppWorkspace] render diagnostics", {
      module: activeTab,
      route: window.location.pathname,
      user: currentUser.email || currentUser.uid,
    });
  }

  return (
    <Layout
      activeTab={activeTab}
      setActiveTab={onNavigateToTab}
      currentUser={currentUser}
      onLogout={onLogout}
      plan={plan}
      onOpenSubscribeClick={onOpenSubscribeClick}
      professionalVerification={DEFAULT_PROFESSIONAL_VERIFICATION}
      isProfessionalAccessBypassed={isProfessionalAccessBypassed}
    >
      {renderContent()}
    </Layout>
  );
};

const AppContent: React.FC<{
  currentUser: User;
  onLogout: () => Promise<void>;
  plan: PlanInfo;
  isProfessionalAccessBypassed: boolean;
  onOpenSubscribeClick?: (tier?: SubscribeTier) => void;
}> = (props) => {
  const [activeTab, setActiveTab] = useState<AppTab>(AppTab.PATIENTS);
  const routeName = typeof window !== "undefined" ? window.location.pathname : "unknown";

  return (
    <DiagnosticErrorBoundary
      moduleName={String(activeTab)}
      routeName={routeName}
    >
      <AppWorkspace
        {...props}
        activeTab={activeTab}
        onNavigateToTab={setActiveTab}
      />
    </DiagnosticErrorBoundary>
  );
};

const Main: React.FC = () => {
  const { t } = useLanguage();
  const { user, loading, logout, refreshUser } = useAuth();
  const [hasAdminClaim, setHasAdminClaim] = useState(false);
  
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [hideAppBehindSubscribe, setHideAppBehindSubscribe] = useState(false);
  const [isReconcilingCheckout, setIsReconcilingCheckout] = useState(false);
  const [checkoutExpectedTier, setCheckoutExpectedTier] = useState<string | null>(null);

  const [subscribeInitialTier, setSubscribeInitialTier] = useState<SubscribeTier>("pro");
  const [subscribeInitialBilling, setSubscribeInitialBilling] = useState<SubscribeBilling>("monthly");
  // isProfessionalAccessBypassed também é usado abaixo para pular o gate de verificação de
  // e-mail (ver ADMIN_EMAIL em utils/adminAccess.ts) — bypass intencional para a conta
  // admin/dono, preservado conforme decisão do time.
  const isProfessionalAccessBypassed = isAdminAccessAllowed(
    user?.email,
    undefined,
    { admin: hasAdminClaim }
  );
  const requiresEmailVerification = requiresEmailVerificationAccess(user) && !isProfessionalAccessBypassed;
  const { plan, loadingPlan } = useUserPlan(requiresEmailVerification ? null : user);

  useEffect(() => {
    let cancelled = false;

    const loadAdminClaim = async () => {
      if (!user) {
        setHasAdminClaim(false);
        return;
      }

      const nextHasAdminClaim = await getAdminAccessClaim(user);
      if (!cancelled) {
        setHasAdminClaim(nextHasAdminClaim);
      }
    };

    void loadAdminClaim();

    return () => {
      cancelled = true;
    };
  }, [user]);

 
  

  // Read ?plan= and ?billing= from URL set by the landing page pricing cards.
  // Must run once on mount (before auth) so params are captured before login redirect.
  useEffect(() => {
    const url = new URL(window.location.href);
    const planParam = url.searchParams.get("plan");
    const billingParam = url.searchParams.get("billing");

    if (planParam === "pro" || planParam === "premium") {
      localStorage.setItem("subscribe_plan", planParam);
      localStorage.setItem("subscribe_billing", billingParam === "annual" ? "annual" : "monthly");
      localStorage.setItem("open_subscribe_after_login", "true");
      url.searchParams.delete("plan");
      url.searchParams.delete("billing");
      window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
    }
  }, []);

  useEffect(() => {
    if (!user || requiresEmailVerification) return;

    const shouldOpen = localStorage.getItem("open_subscribe_after_login");

    if (shouldOpen === "true") {
      const savedPlan = localStorage.getItem("subscribe_plan") as SubscribeTier | null;
      const savedBilling = localStorage.getItem("subscribe_billing") as SubscribeBilling | null;

      if (savedPlan === "pro" || savedPlan === "premium") {
        setSubscribeInitialTier(savedPlan);
      }
      setSubscribeInitialBilling(savedBilling === "annual" ? "annual" : "monthly");

      setHideAppBehindSubscribe(true);
      setSubscribeOpen(true);
      localStorage.removeItem("open_subscribe_after_login");
      localStorage.removeItem("subscribe_plan");
      localStorage.removeItem("subscribe_billing");
    }
  }, [user, requiresEmailVerification]);

  useEffect(() => {
    if (!user || loading || requiresEmailVerification) return;

    const currentUrl = new URL(window.location.href);
    const checkoutState = currentUrl.searchParams.get("checkout");
    const sessionId = currentUrl.searchParams.get("session_id");

    if (checkoutState !== "success" || !sessionId) return;

    let cancelled = false;

    const clearCheckoutParams = () => {
      const nextUrl = new URL(window.location.href);
      nextUrl.searchParams.delete("checkout");
      nextUrl.searchParams.delete("session_id");
      window.history.replaceState({}, "", `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
    };

    const wait = (ms: number) =>
      new Promise((resolve) => window.setTimeout(resolve, ms));

    const reconcile = async () => {
      setIsReconcilingCheckout(true);

      for (let attempt = 0; attempt < 8; attempt += 1) {
        try {
          const token = await user.getIdToken();
          const resp = await fetch(
            "https://us-central1-orthoprecision-web.cloudfunctions.net/reconcileCheckoutSessionHttp",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({ sessionId }),
            }
          );

          const data = await resp.json().catch(() => ({}));
          if (!resp.ok) {
            throw new Error(data?.error || "Falha ao reconciliar checkout");
          }

          const status = String(data?.status || "none");
          if (status === "active") {
            setCheckoutExpectedTier(typeof data?.tier === "string" ? data.tier : null);
            clearCheckoutParams();
            if (!cancelled) setIsReconcilingCheckout(false);
            return;
          }
        } catch (error) {
          console.error("Erro ao reconciliar checkout:", error);
        }

        await wait(2000);
      }

      clearCheckoutParams();
      if (!cancelled) setIsReconcilingCheckout(false);
    };

    void reconcile();

    return () => {
      cancelled = true;
    };
  }, [user, loading, requiresEmailVerification]);

  useEffect(() => {
    if (!checkoutExpectedTier) return;
    if (plan?.tier === checkoutExpectedTier) {
      setCheckoutExpectedTier(null);
    }
  }, [checkoutExpectedTier, plan]);

  const handleOpenSubscribe = async ({
    tier,
    interval,
    plan,
    method,
    priceId,
    successUrl,
    cancelUrl,
  }: {
    // Novo formato (SubscribeModal) — backend resolve priceId internamente
    tier?: string;
    interval?: string;
    // Formato legado (LoginScreen) — envia priceId explícito
    plan?: string;
    priceId?: string;
    // Comum
    method: "credit_card" | "pix";
    successUrl: string;
    cancelUrl: string;
  }) => {
    try {
      if (!user) throw new Error("Usuário não autenticado.");

      const token = await user.getIdToken();

      const endpoint =
        method === "pix"
          ? "https://us-central1-orthoprecision-web.cloudfunctions.net/createPixCheckoutSessionHttp"
          : "https://us-central1-orthoprecision-web.cloudfunctions.net/createCheckoutSessionHttp";

      // Monta o body conforme o formato recebido
      const body: Record<string, string | undefined> = { successUrl, cancelUrl };
      if (tier && interval) {
        // Novo formato: backend escolhe o priceId via stripePlans.ts
        body.tier = tier;
        body.interval = interval;
      } else {
        // Legado: priceId explícito (LoginScreen, fluxos antigos)
        body.priceId = priceId;
        if (plan) body.plan = plan;
      }

      const resp = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.error || "Falha ao criar checkout");

      window.location.href = data.url;
    } catch (error) {
      console.error("Erro ao iniciar checkout:", error);
      alert(error instanceof Error ? error.message : "Erro ao iniciar pagamento.");
    }
  };

  if (loading) return <div>{t("appLoading")}</div>;

  if (!user) {
    return <LoginScreen onOpenSubscribe={handleOpenSubscribe} />;
  }

  if (requiresEmailVerification) {
    return (
      <EmailVerificationGate
        currentUser={user}
        onRefreshUser={refreshUser}
        onUseAnotherAccount={logout}
      />
    );
  }

  if (isReconcilingCheckout) return <div>{t("loadingPlan")}</div>;

  if (checkoutExpectedTier && plan?.tier !== checkoutExpectedTier) {
    return <div>{t("loadingPlan")}</div>;
  }

  if (loadingPlan) return <div>{t("loadingPlan")}</div>;

  
  return (
    <>
      {!hideAppBehindSubscribe && (
        <AppContent
          currentUser={user}
          onLogout={logout}
          plan={plan as PlanInfo}
          isProfessionalAccessBypassed={isProfessionalAccessBypassed}
          onOpenSubscribeClick={() => {
            setSubscribeInitialTier("pro");
            setSubscribeInitialBilling("monthly");
            setSubscribeOpen(true);
          }}
        />
      )}

      <SubscribeModal
        open={subscribeOpen}
        initialTier={subscribeInitialTier}
        initialBilling={subscribeInitialBilling}
        onClose={async () => {
          setSubscribeOpen(false);
          localStorage.removeItem("open_subscribe_after_login");

          if (hideAppBehindSubscribe) {
            setHideAppBehindSubscribe(false);
            await logout();
            return;
          }
        }}
        onOpenSubscribe={handleOpenSubscribe}
      />
    </>
  );
};

const App: React.FC = () => {
  const pathname =
    typeof window !== "undefined" ? window.location.pathname.replace(/\/+$/, "") || "/" : "";
  const matchesRoute = (paths: string[]) =>
    paths.some((route) => pathname === route || pathname.startsWith(`${route}/`));
  const isAdminRoute =
    typeof window !== "undefined" && matchesRoute(["/admin"]);
  const verifyReportMatch =
    typeof window !== "undefined"
      ? pathname.match(/^\/verify-report\/([^/]+)$/)
      : null;
  const isAppRoute =
    typeof window !== "undefined" && matchesRoute(["/app"]);
  const isPrivacyRoute =
    typeof window !== "undefined" &&
    matchesRoute(["/privacy", "/privacy-policy"]);
  const isSupportRoute =
    typeof window !== "undefined" &&
    matchesRoute(["/suporte", "/support"]);
  const isTermsRoute =
    typeof window !== "undefined" &&
    matchesRoute(["/terms", "/terms-of-use"]);

  return (
    <LanguageProvider>
      {isAdminRoute ? (
        <AuthProvider>
          <AdminPanel />
        </AuthProvider>
      ) : verifyReportMatch ? (
        <VerifyReportPage documentId={decodeURIComponent(verifyReportMatch[1])} />
      ) : isPrivacyRoute ? (
        <PrivacyPolicyPage />
      ) : isSupportRoute ? (
        <SupportPage />
      ) : isTermsRoute ? (
        <TermsOfUsePage />
      ) : isAppRoute ? (
        <AuthProvider>
          <PatientProvider>
            <Main />
          </PatientProvider>
        </AuthProvider>
      ) : (
        <LandingPage />
      )}
    </LanguageProvider>
  );
};

export default App;
